/**
 * MobileOnboarding
 * 3-slide carousel shown ONCE on first native app launch, before the login screen.
 */
import React, { useState } from 'react';

import jobHuntSvg from '../../assets/illustrations/onboarding-job-hunt.svg';
import resumeSvg from '../../assets/illustrations/onboarding-resume.svg';
import trackingSvg from '../../assets/illustrations/onboarding-tracking.svg';

const STORAGE_KEY = 'ggj_native_onboarded';

// ── Slide data ────────────────────────────────────────────────────────────────

interface Slide {
  gradient: string;
  accentGradient: string;
  img: string;
  label: string;
  title: string;
  subtitle: string;
}

const SLIDES: Slide[] = [
  {
    gradient: 'linear-gradient(155deg, #6C47FF 0%, #4335CC 60%, #3B28B8 100%)',
    accentGradient: 'linear-gradient(135deg, #6C47FF 0%, #4F35CC 100%)',
    img: jobHuntSvg,
    label: 'Job Discovery',
    title: 'Find Your Dream Job',
    subtitle: 'Browse thousands of opportunities matched to your skills and experience — all in one place.',
  },
  {
    gradient: 'linear-gradient(155deg, #7C5CFF 0%, #5B3FCC 60%, #4B30BB 100%)',
    accentGradient: 'linear-gradient(135deg, #7C5CFF 0%, #5B3FCC 100%)',
    img: resumeSvg,
    label: 'AI-Powered',
    title: 'Optimize Your Resume',
    subtitle: 'Our AI rewrites your resume for every specific job role so you pass ATS filters every time.',
  },
  {
    gradient: 'linear-gradient(155deg, #5B6FFF 0%, #6C47FF 60%, #5538DD 100%)',
    accentGradient: 'linear-gradient(135deg, #5B6FFF 0%, #6C47FF 100%)',
    img: trackingSvg,
    label: 'Stay Organized',
    title: 'Track Every Application',
    subtitle: 'Know exactly where you stand at every stage — from Applied all the way to Offer.',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface MobileOnboardingProps {
  onDone: () => void;
}

export function hasCompletedOnboarding(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

function markOnboardingDone(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // ignore
  }
}

const MobileOnboarding: React.FC<MobileOnboardingProps> = ({ onDone }) => {
  const [index, setIndex] = useState(0);

  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  const go = (next: number) => setIndex(next);

  const finish = () => {
    markOnboardingDone();
    onDone();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        /* Top uses header theme (light purple / dark gradient); bottom card uses layered card vars */
        background: 'var(--ggj-header-bg, var(--vscode-bg-primary))',
        transition: 'background 0.2s ease',
        paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Skip button */}
      {!isLast && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px 0' }}>
          <button
            type="button"
            onClick={finish}
            style={{
              background: 'rgba(255,255,255,0.22)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 20,
              padding: '7px 18px',
              color: 'rgba(255,255,255,0.95)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Skip
          </button>
        </div>
      )}

      {/* Illustration area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 24px 0',
        }}
      >
        <img
          src={slide.img}
          alt={slide.title}
          style={{
            width: '100%',
            maxWidth: 320,
            height: 'auto',
            maxHeight: 280,
            objectFit: 'contain',
            filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.18))',
          }}
        />
      </div>

      {/* Bottom card — layered card theme (light/dark mockup) */}
      <div
        style={{
          background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-elevated))',
          borderRadius: '24px 24px 0 0',
          padding: '28px 24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: 'var(--ggj-mobile-content-card-shadow, var(--vscode-shadow-lg))',
          borderTop: 'var(--ggj-mobile-content-card-border-top, 1px solid var(--vscode-border-primary))',
        }}
      >
        <div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--brand-accent-bg)',
            border: '1px solid var(--brand-accent-border)',
            borderRadius: 20,
            padding: '4px 12px',
            marginBottom: 10,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--vscode-primary)' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--vscode-primary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {slide.label}
            </span>
          </div>

          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--vscode-text-primary)', marginBottom: 8, lineHeight: 1.2, letterSpacing: '-0.03em' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--vscode-text-secondary)', lineHeight: 1.65, margin: 0 }}>
            {slide.subtitle}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {SLIDES.map((_, i) => (
              <div
                key={i}
                onClick={() => go(i)}
                style={{
                  width: i === index ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === index ? 'var(--vscode-primary)' : 'var(--vscode-border-primary)',
                  transition: 'width 0.2s ease, background 0.2s ease',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={isLast ? finish : () => go(index + 1)}
            style={{
              height: 52,
              padding: '0 32px',
              borderRadius: 14,
              background: 'var(--brand-cta-bg)',
              border: '1px solid var(--brand-cta-border)',
              color: 'var(--brand-cta-text)',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '-0.01em',
              boxShadow: 'var(--brand-cta-shadow)',
              WebkitTapHighlightColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {isLast ? 'Get Started' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileOnboarding;
